import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Refresh server-rendered facilitator components using the group's existing stream.
export default class extends Controller {
  static targets = ["content", "status"]
  static values = { url: String, activityId: String, groupId: String, commandUrl: String }

  connect() {
    this.abort = new AbortController()
    this.consumer = createConsumer()
    this.consumer.subscriptions.create({ channel: "GroupWorkshopChannel", activity_id: this.activityIdValue, group_id: this.groupIdValue }, {
      received: () => this.refresh(),
      connected: () => this.refresh(),
      disconnected: () => { this.statusTarget.textContent = "Reconnecting…" }
    })
    this.timer = setInterval(() => this.refresh(), 5000)
  }

  disconnect() {
    this.abort.abort()
    this.consumer.disconnect()
    clearInterval(this.timer)
  }

  async command(event) {
    event.preventDefault()
    const button = event.currentTarget
    if (this.sendingCommand || button.disabled) return
    this.sendingCommand = true
    button.disabled = true
    const errorBox = this.element.querySelector("[data-group-action-error]")
    errorBox.textContent = ""
    try {
      const response = await fetch(this.commandUrlValue, {
        method: "PATCH", credentials: "same-origin", signal: this.abort.signal,
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ group_id: this.groupIdValue, operation: button.dataset.operation, ignored: button.dataset.ignored === "true" })
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || "Unable to update the group.")
      }
      const modal = button.closest('[data-controller="modal"]')
      if (modal) modal.dataset.modalOpenValue = "false"
      await this.refresh()
    } catch (error) { if (error.name !== "AbortError") errorBox.textContent = error.message }
    finally { this.sendingCommand = false; button.disabled = false }
  }

  async refresh() {
    if (this.loading || this.abort.signal.aborted) return
    this.loading = true
    try {
      const response = await fetch(this.urlValue, { signal: this.abort.signal, headers: { Accept: "application/json" } })
      if (!response.ok) {
        if ([401, 403, 404].includes(response.status)) {
          this.contentTarget.replaceChildren()
          this.disconnect()
        }
        throw new Error("Unable to load conversation.")
      }
      const { html } = await response.json()
      if (this.abort.signal.aborted) return
      const fragment = document.createElement("template")
      fragment.innerHTML = html
      const messages = this.contentTarget.querySelector("[data-group-messages]")
      const scroll = messages.scrollTop
      const atBottom = messages.scrollHeight - scroll - messages.clientHeight < 80
      const tab = this.element.querySelector('[data-tabs-target="trigger"][data-active="true"]')?.dataset.value || "progress"
      for (const region of this.element.querySelectorAll("[data-live-region]")) {
        const next = fragment.content.querySelector(`[data-live-region="${region.dataset.liveRegion}"]`)
        if (!next || region.querySelector('[data-modal-open-value="true"]')) continue
        if (region.dataset.liveRegion === "messages" && region.querySelector('[data-message-edit-target="editMode"]:not(.hidden)')) continue
        // Keep inputs, active dialogs and scroll containers mounted during live updates.
        if (region.dataset.serverHTML !== next.innerHTML) {
          region.dataset.serverHTML = next.innerHTML
          region.replaceChildren(...next.childNodes)
        }
      }
      const canIntervene = fragment.content.querySelector("[data-can-intervene]").dataset.canIntervene === "true"
      this.element.querySelectorAll("[data-group-intervention-footer] button, [data-group-intervention-footer] textarea").forEach(input => { input.disabled = !canIntervene })
      this.application.getControllerForElementAndIdentifier(this.element, "tabs")?.activateTab(tab, false)
      messages.scrollTop = atBottom ? messages.scrollHeight : scroll
      this.statusTarget.textContent = "Live"
    } catch (error) {
      if (!this.abort.signal.aborted) this.statusTarget.textContent = "Reconnecting…"
      else if (error.name !== "AbortError") this.statusTarget.textContent = "Conversation access is no longer available."
    } finally { this.loading = false }
  }
}
